<template>
  <div :class="rootClasses">
    <!-- Authenticated Layout -->
    <template v-if="isAuthenticated && !isAuthRoute">
      <AppSidebar :is-sidebar-collapsed="isSidebarCollapsed" />
      <AppHeader 
        @toggle-sidebar="toggleSidebar" 
        @toggle-mobile-menu="toggleMobileMenu" 
      />
      <div class="pc-container">
        <div class="pc-content">
          <!-- Page Content -->
          <router-view />
        </div>
      </div>
      <AppFooter />
    </template>
    
    <!-- Auth Pages or Not Authenticated -->
    <template v-else>
      <router-view />
    </template>
    
    <!-- Language Switcher -->
    <LanguageSwitcher />
    
    <!-- Notifications -->
    <Notifications />

    <!-- Mobile overlay to close sidebar when open -->
    <div 
      v-if="!isSidebarCollapsed && isAuthenticated && !isAuthRoute" 
      class="app-overlay" 
      @click="closeSidebar"
    ></div>

    <!-- Impersonation frame -->
    <div 
      v-if="isImpersonating" 
      class="impersonation-frame" 
      aria-hidden="true"
    ></div>

    <!-- Impersonation banner -->
    <button
      v-if="isImpersonating"
      type="button"
      class="impersonation-banner"
      @click="handleGlobalStopImpersonation"
      :disabled="isStoppingImpersonation"
    >
      <i class="ph-duotone ph-user-switch me-2"></i>
      {{ $t('admin.stopImpersonation') }}
    </button>

    <!-- User Messages Banner -->
    <UserMessageBanner v-if="isAuthenticated && !isAuthRoute" />
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import AppSidebar from '@/components/AppSidebar.vue';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import Notifications from '@/components/Notifications.vue';
import UserMessageBanner from '@/components/UserMessageBanner.vue';
import { Toast } from 'bootstrap';
import authService from '@/services/authService';
import { usePresence } from '@/composables/usePresence';

export default {
  name: 'App',
  components: {
    AppSidebar,
    AppHeader,
    AppFooter,
    LanguageSwitcher,
    Notifications,
    UserMessageBanner
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { t } = useI18n();
    
    // Sidebar state
    const isSidebarCollapsed = ref(false);
    const isMobileMenuOpen = ref(false);
    
    // Auth state
    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated']);
    const notification = computed(() => store.state.notification);
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const isImpersonating = computed(() => currentUser.value?.isImpersonating === true);
    const isStoppingImpersonation = ref(false);
    
    // Presence tracking
    const { startPresence, stopPresence } = usePresence();
    
    const rootClasses = computed(() => ({
      'sidebar-mini': isSidebarCollapsed.value,
      'impersonation-active': isImpersonating.value
    }));
    
    // Check if current route is an auth route
    const isAuthRoute = computed(() => {
      const authRoutes = ['/login', '/register', '/forgot-password', '/reset-password'];
      return authRoutes.includes(route.path);
    });
    
    // Toggle sidebar collapsed state
    const toggleSidebar = () => {
      isSidebarCollapsed.value = !isSidebarCollapsed.value;
    };
    
    // Toggle mobile menu
    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
      // Use common collapse state for all sizes
      isSidebarCollapsed.value = !isSidebarCollapsed.value;
    };
    
    // Handle unauthorized event (401 response)
    const handleUnauthorized = () => {
      // Show notification
      store.dispatch('notify', {
        message: 'Your session has expired. Please log in again.',
        type: 'warning'
      });
      
      // Redirect to login
      router.push('/login');
    };
    
    // Watch for route changes to close mobile menu
    watch(() => route.path, () => {
      if (isMobileMenuOpen.value) isMobileMenuOpen.value = false;
      const headerContent = document.querySelector('.header-content');
      if (headerContent) headerContent.classList.remove('show');
      // Do not force-collapse on route change; keep user's sidebar state
    });
    

    
    // Initialize feather icons
    const initFeatherIcons = () => {
      if (window.feather) {
        window.feather.replace();
      }
    };
    
    // Show notification toast
    watch(notification, (newVal) => {
      if (newVal?.message) {
        const toastEl = document.querySelector('.toast');
        if (toastEl) {
          const toast = new Toast(toastEl);
          toast.show();
          
          // Auto hide after 5 seconds
          setTimeout(() => {
            toast.hide();
          }, 5000);
        }
      }
    });
    
    // Watch route changes to update body class
    watch(() => route.path, () => {
    
      
      // Re-initialize feather icons when route changes
      nextTick(() => {
        initFeatherIcons();
      });
    });
    
    onMounted(() => {
    
      initFeatherIcons();
      
      // Start presence tracking if authenticated
      if (isAuthenticated.value) {
        startPresence();
      }
      
      // Add listener for unauthorized event
      window.addEventListener('unauthorized', handleUnauthorized);
      // ESC closes mobile sidebar
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          isMobileMenuOpen.value = false;
          isSidebarCollapsed.value = true;
        }
      });
    });
    
    onUnmounted(() => {
      // Stop presence tracking
      stopPresence();
      
      // Remove listener for unauthorized event
      window.removeEventListener('unauthorized', handleUnauthorized);
    });
    
    // Watch authentication state changes
    watch(isAuthenticated, (newVal) => {
      if (newVal) {
        // User logged in - start presence tracking
        nextTick(() => {
          startPresence();
        });
      } else {
        // User logged out - stop presence tracking
        stopPresence();
      }
    });
    
    const getNotificationIcon = (type) => {
      switch(type) {
        case 'success':
          return 'ph-duotone ph-check-circle';
        case 'danger':
        case 'error':
          return 'ph-duotone ph-x-circle';
        case 'warning':
          return 'ph-duotone ph-warning-circle';
        case 'info':
        default:
          return 'ph-duotone ph-info';
      }
    };
    
    const closeSidebar = () => {
      isMobileMenuOpen.value = false;
      isSidebarCollapsed.value = true;
    };

    const handleGlobalStopImpersonation = async () => {
      if (isStoppingImpersonation.value) return;
      isStoppingImpersonation.value = true;
      try {
        const result = await authService.stopImpersonation();
        if (result?.user) {
          store.commit('auth/SET_USER', result.user);
        }
        if (result?.token) {
          store.commit('auth/SET_TOKEN', result.token);
        }
        
        store.dispatch('notify', {
          message: t('common.notification.impersonationStopped'),
          type: 'success'
        });
        
        window.location.href = '/admin';
      } catch (error) {
        console.error('Global stop impersonation error:', error);
        store.dispatch('notify', {
          message: 'Failed to stop user impersonation',
          type: 'error'
        });
      } finally {
        isStoppingImpersonation.value = false;
      }
    };
    
    return {
      isAuthenticated,
      isAuthRoute,
      isSidebarCollapsed,
      toggleSidebar,
      toggleMobileMenu,
      closeSidebar,
      notification,
      getNotificationIcon,
      rootClasses,
      isImpersonating,
      isStoppingImpersonation,
      handleGlobalStopImpersonation
    };
  }
};
</script>

<style>
/* Breadcrumb styles */
.breadcrumb {
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
  list-style: none;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  cursor: pointer;
}

.breadcrumb-item a {
  color: var(--bs-primary);
  text-decoration: none;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: all 0.2s;
  display: block;
  width: 100%;
}

.breadcrumb-item a:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.08);
  color: var(--bs-primary);
}

.breadcrumb-item + .breadcrumb-item::before {
  content: "/";
  padding: 0 0.5rem;
  color: var(--bs-gray-600);
}

.breadcrumb-item.active {
  color: var(--bs-gray-600);
  cursor: default;
}

.breadcrumb-item.active a {
  color: var(--bs-gray-600);
  pointer-events: none;
}

.breadcrumb-item.active a:hover {
  background-color: transparent;
}

/* Auth page styles */


.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

main {
  flex: 1;
  padding-top: 1rem;
}

/* Mobile overlay shown when sidebar is open */
.app-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.15);
  z-index: 1028;
  display: none;
}

@media (max-width: 1024px) {
  .app-overlay { display: block; }
}

.impersonation-active {
  position: relative;
}

.impersonation-frame {
  pointer-events: none;
  position: fixed;
  inset: 0;
  border: 5px solid rgba(161, 3, 19, 0.8);
  border-radius: 3px;
  box-sizing: border-box;
  z-index: 4000;
  animation: impersonationPulse 1.5s ease-in-out infinite;
  box-shadow: 0 0 20px rgba(236, 3, 27, 0.6);
}

@keyframes impersonationPulse {
  0% {
    opacity: 0.7;
    box-shadow: 0 0 12px rgba(220, 53, 69, 0.6);
  }
  50% {
    opacity: 1;
    box-shadow: 0 0 25px rgba(220, 53, 69, 0.9);
  }
  100% {
    opacity: 0.7;
    box-shadow: 0 0 12px rgba(220, 53, 69, 0.6);
  }
}

.impersonation-banner {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4001;
  background: #a10313;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 0.55rem 1.5rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 0 18px rgba(161, 3, 19, 0.65);
  animation: impersonationBannerPulse 1s ease-in-out infinite;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.impersonation-banner:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

@keyframes impersonationBannerPulse {
  0% {
    transform: translateX(-50%) scale(1);
  }
  50% {
    transform: translateX(-50%) scale(1.04);
  }
  100% {
    transform: translateX(-50%) scale(1);
  }
}

</style> 